// @ts-nocheck
/**
 * Director-written content tabs shown above the public schedule
 * (event_schedule_tabs). Add, rename in place, rich-text body, drag to
 * reorder, visible/hidden, delete with confirm, and copy tabs in from
 * another event of the same organisation (append only).
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BasicRichTextEditor, isEmptyNoteHtml } from "@/components/editor/BasicRichTextEditor";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";

const db = supabase as any;

export function ScheduleTabsEditor({ eventId }: { eventId: string }) {
  const [tabs, setTabs] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [toDelete, setToDelete] = useState<any>(null);

  const load = async () => {
    const { data, error } = await db.from("event_schedule_tabs").select("*").eq("event_id", eventId)
      .order("sort_order", { ascending: true }).order("created_at", { ascending: true });
    if (error) toast.error(error.message);
    setTabs(data || []);
    setLoaded(true);
  };
  useEffect(() => {
    load();
    db.from("events").select("organization_id").eq("id", eventId).maybeSingle()
      .then(({ data }) => setOrgId(data?.organization_id || null));
  }, [eventId]);

  const nextOrder = () => (tabs.length ? Math.max(...tabs.map((t) => t.sort_order || 0)) + 1 : 0);

  const addTab = async () => {
    const title = newTitle.trim();
    if (!title) return;
    const { error } = await db.from("event_schedule_tabs").insert({
      event_id: eventId, title, body_html: isEmptyNoteHtml(newBody) ? null : newBody, sort_order: nextOrder(),
    });
    if (error) return toast.error(error.message);
    setNewTitle(""); setNewBody(""); setAdding(false);
    load();
  };

  const patch = async (id: string, values: any) => {
    setTabs((ts) => ts.map((t) => (t.id === id ? { ...t, ...values } : t)));
    const { error } = await db.from("event_schedule_tabs").update(values).eq("id", id);
    if (error) { toast.error(error.message); load(); return false; }
    return true;
  };

  const remove = async () => {
    const t = toDelete; setToDelete(null);
    const { error } = await db.from("event_schedule_tabs").delete().eq("id", t.id);
    if (error) return toast.error(error.message);
    load();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = tabs.findIndex((t) => t.id === active.id);
    const to = tabs.findIndex((t) => t.id === over.id);
    const next = arrayMove(tabs, from, to).map((t, i) => ({ ...t, sort_order: i }));
    setTabs(next);
    const results = await Promise.all(next.map((t) => db.from("event_schedule_tabs").update({ sort_order: t.sort_order }).eq("id", t.id)));
    const bad = results.find((r) => r.error);
    if (bad) { toast.error(bad.error.message); load(); }
  };

  return (
    <section className="mt-4 w-full space-y-3 rounded-lg border p-3 text-sm" data-testid="schedule-tabs-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-medium">Public schedule tabs</h3>
          <p className="text-xs text-muted-foreground">
            {tabs.some((t) => t.is_visible)
              ? "Visible tabs show above the filters on the public schedule."
              : "No visible tabs — nothing shows above the public schedule."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CopyTabsFrom eventId={eventId} orgId={orgId} startOrder={nextOrder()} onCopied={load} />
          <Button size="sm" onClick={() => setAdding(true)} disabled={adding}><Plus className="mr-1 h-4 w-4" />Add tab</Button>
        </div>
      </div>

      {adding && (
        <div className="space-y-2 rounded-md border border-dashed p-3">
          <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Tab name" aria-label="New tab name" autoFocus />
          <BasicRichTextEditor value={newBody} onChange={setNewBody} variant="note" ariaLabel="New tab content" />
          <div className="flex gap-2">
            <Button size="sm" onClick={addTab} disabled={!newTitle.trim()}>Add tab</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewTitle(""); setNewBody(""); }}>Cancel</Button>
          </div>
        </div>
      )}

      {loaded && tabs.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">No tabs yet.</p>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={tabs.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {tabs.map((t) => (
              <TabRow key={t.id} tab={t} onPatch={patch} onDelete={() => setToDelete(t)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the "{toDelete?.title}" tab?</AlertDialogTitle>
            <AlertDialogDescription>Its content will be removed from this event. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Delete tab</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function TabRow({ tab, onPatch, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.id });
  const [title, setTitle] = useState(tab.title);
  const [body, setBody] = useState(tab.body_html || "");
  const [savedBody, setSavedBody] = useState(tab.body_html || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTitle(tab.title); }, [tab.title]);

  const saveTitle = async () => {
    const v = title.trim();
    if (!v) { setTitle(tab.title); return; }
    if (v !== tab.title) await onPatch(tab.id, { title: v });
  };
  const saveBody = async () => {
    setBusy(true);
    const value = isEmptyNoteHtml(body) ? null : body;
    if (await onPatch(tab.id, { body_html: value })) { setSavedBody(body); toast.success("Tab saved"); }
    setBusy(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`space-y-2 rounded-md border bg-background p-3 ${isDragging ? "opacity-70 shadow-lg" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="cursor-grab touch-none p-1 text-muted-foreground" aria-label={`Drag to reorder ${tab.title}`} {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          className="h-8 min-w-0 flex-1 font-medium"
          aria-label="Tab name"
        />
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={tab.is_visible} onCheckedChange={(v) => onPatch(tab.id, { is_visible: v })} aria-label="Visible on public schedule" />
          {tab.is_visible ? "Visible" : "Hidden"}
        </label>
        <Button size="sm" variant="ghost" onClick={onDelete} aria-label={`Delete ${tab.title}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <BasicRichTextEditor value={body} onChange={setBody} variant="note" ariaLabel={`${tab.title} content`} />
      {body !== savedBody && (
        <div className="flex gap-2">
          <Button size="sm" onClick={saveBody} disabled={busy}>{busy ? "Saving…" : "Save tab"}</Button>
          <Button size="sm" variant="ghost" onClick={() => setBody(savedBody)} disabled={busy}>Discard changes</Button>
        </div>
      )}
    </div>
  );
}

function CopyTabsFrom({ eventId, orgId, startOrder, onCopied }) {
  const [rows, setRows] = useState<any[]>([]);
  const [source, setSource] = useState<string>("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    db.from("event_schedule_tabs")
      .select("id, title, body_html, sort_order, is_visible, event_id, events!inner(id, name, start_date, organization_id)")
      .eq("events.organization_id", orgId)
      .neq("event_id", eventId)
      .order("sort_order", { ascending: true })
      .then(({ data }) => setRows(data || []));
  }, [orgId, eventId]);

  const events = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of rows) if (!m.has(r.event_id)) m.set(r.event_id, r.events);
    return [...m.values()].sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")));
  }, [rows]);
  const sourceTabs = rows.filter((r) => r.event_id === source);

  if (!events.length) return null;

  const choose = (id: string) => {
    setSource(id);
    setPicked(new Set(rows.filter((r) => r.event_id === id).map((r) => r.id)));
  };
  const copy = async () => {
    const chosen = sourceTabs.filter((t) => picked.has(t.id));
    if (!chosen.length) return;
    setBusy(true);
    const { error } = await db.from("event_schedule_tabs").insert(
      chosen.map((t, i) => ({ event_id: eventId, title: t.title, body_html: t.body_html, is_visible: t.is_visible, sort_order: startOrder + i })),
    );
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success(`${chosen.length} tab${chosen.length === 1 ? "" : "s"} copied`);
    setSource(""); setPicked(new Set());
    onCopied();
  };

  return (
    <div className="w-full space-y-2 sm:w-auto">
      <Select value={source} onValueChange={choose}>
        <SelectTrigger className="h-9 w-full sm:w-64"><SelectValue placeholder="Copy tabs from…" /></SelectTrigger>
        <SelectContent>
          {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {source && (
        <div className="space-y-2 rounded-md border p-2">
          {sourceTabs.map((t) => (
            <label key={t.id} className="flex items-center gap-2">
              <Checkbox
                checked={picked.has(t.id)}
                onCheckedChange={(v) => setPicked((p) => { const n = new Set(p); v ? n.add(t.id) : n.delete(t.id); return n; })}
              />
              {t.title}{!t.is_visible && <span className="text-xs text-muted-foreground">(hidden)</span>}
            </label>
          ))}
          <div className="flex gap-2">
            <Button size="sm" onClick={copy} disabled={busy || picked.size === 0}>
              {busy ? "Copying…" : `Copy ${picked.size} tab${picked.size === 1 ? "" : "s"}`}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSource("")}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ScheduleTabsEditor;
